import {observer} from 'mobx-react';
import {Text} from '../../styles/text';
import {primaryColor} from '../../utils/variables';
import AppStore from '../../app.store';
import {createPortal} from "react-dom";
import {useEffect, useRef, useId, useState} from "react";
import {
    CloseIcon,
    ModalBody,
    ModalContainer,
    ModalGlobalStyles,
    ModalHeader,
    ModalInner,
} from './modal.style';
import {Layout} from "../../styles/layout";
import {lockBodyScroll} from "../../utils/bodyScrollLock";
import {registerModal, isLastModal, getModalStackIndex} from "../../utils/modalHistory";

interface ModalProps {
    children: JSX.Element;
    title?: string;
    onClose: (clearData: boolean) => void;
    mobile?: boolean;
    fullScreen?: boolean;
    /** При fullScreen: отступы и вертикальный скролл у тела модалки вместо overflow:hidden */
    fullScreenScrollable?: boolean;
    fitContent?: boolean;
    hasContext?: boolean;
    large?: boolean;
    noBackdrop?: boolean;
}

const ModalView = observer(({
                                children,
                                title,
                                onClose,
                                mobile,
                                fullScreen,
                                fullScreenScrollable,
                                fitContent,
                                large,
                                hasContext,
                                noBackdrop
                            }: ModalProps) => {
    const modalId = useId();
    const isClosingByBackButtonRef = useRef(false);
    const onCloseRef = useRef(onClose);
    const [stackIndex, setStackIndex] = useState(0);
    
    // Обновляем ref при изменении onClose
    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);
    
    useEffect(() => {
        const unlockScroll = lockBodyScroll();
        const unregisterModal = registerModal(modalId);
        setStackIndex(getModalStackIndex(modalId));

        // Обработчик кнопки "назад"
        const handlePopState = (event: PopStateEvent) => {
            if (!isLastModal(modalId)) return;

            // If history state still points to this modal, do not close it.
            // This allows internal wizards to use back navigation.
            if ((event.state as any)?.modalOpen && (event.state as any)?.modalId === modalId) {
                return;
            }

            isClosingByBackButtonRef.current = true;
            onCloseRef.current(true);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.code === 'Escape' && isLastModal(modalId)) {
                event.preventDefault();
                onCloseRef.current(true);
            }
        };

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            unlockScroll();
            unregisterModal();
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('keydown', handleKeyDown);
            if (!isClosingByBackButtonRef.current && window.history.state?.modalOpen) {
                window.history.replaceState(null, '', window.location.href);
            }
            isClosingByBackButtonRef.current = false;
        };
    }, [modalId]);
    /*return (
        <ModalContainer
            id={'modal-container'}
            onClick={() => onClose(false)}
            mobile={mobile}>
            <ModalInner
                id={'modal-inner'}
                onClick={(e) => e.stopPropagation()}
                fullScreen={fullScreen}
                fitContent={fitContent}
                hasContext={hasContext}
                large={large}
            >
                <ModalHeader id={'modal-header'}>
                    <Text id={'modal-title'} extent="20px" weight="700" color={primaryColor}>
                        {title}
                    </Text>
                    <Layout between={10}>
                        {/!*<MinimizeIcon onClick={() => onClose(false)}/>*!/}
                        <CloseIcon id={'modal-close-icon'} onClick={() => onClose(true)}/>
                    </Layout>
                </ModalHeader>
                <ModalBody id={'modal-body'}>{children}</ModalBody>
            </ModalInner>
            <ModalGlobalStyles/>
        </ModalContainer>
    );*/

    /* with create portal modals will not collide with each other when opened several on top of each other and always renders relative to viewport */
    const [mouseDownOnBackdrop, setMouseDownOnBackdrop] = useState(false);

    return createPortal(
        <ModalContainer
            id="modal-container"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                    setMouseDownOnBackdrop(true);
                } else {
                    setMouseDownOnBackdrop(false);
                }
            }}
            onMouseUp={(e) => {
                if (e.target === e.currentTarget && mouseDownOnBackdrop) {
                    onClose(false);
                }
                setMouseDownOnBackdrop(false);
            }}
            mobile={mobile}
            noBackdrop={noBackdrop}
            fitContent={fitContent}
            stackIndex={stackIndex}
        >
            <ModalInner
                id="modal-inner"
                onClick={(e) => e.stopPropagation()}
                fullScreen={fullScreen}
                fitContent={fitContent}
                hasContext={hasContext}
                large={large}
            >
                <ModalHeader id="modal-header">
                    <Text id="modal-title" extent="20px" weight="700" color={primaryColor}>
                        {title}
                    </Text>
                    <Layout between={10}>
                        {/* <MinimizeIcon onClick={() => onClose(false)} /> */}
                        <CloseIcon id="modal-close-icon" onClick={() => onClose(true)}/>
                    </Layout>
                </ModalHeader>
                <ModalBody
                    id="modal-body"
                    fullScreen={fullScreen}
                    fullScreenScrollable={fullScreenScrollable}
                    fitContent={fitContent}
                    large={large}
                    isMacOS={AppStore.isMacOS}
                >
                    {children}
                </ModalBody>
            </ModalInner>
            <ModalGlobalStyles/>
        </ModalContainer>,
        document.body
    );
});

export default ModalView;
